import Box from 'Components/Atoms/Box';
import Checkbox from 'Components/Atoms/Checkbox';
import Typography from 'Components/Atoms/Typography';
import {
  ReservationActionTypes,
  useReservationContext,
} from 'Contexts/ReservationContext';
import useDocument from 'CustomHooks/useDocument';
import { RelationshipDocument } from 'types/shared/documents';
import React, { useMemo, useEffect } from 'react';
import { Reservation } from 'types/reservations';
import {
  toEuropeanDate,
  capitalizeWord,
  blockToTime,
  noop,
  toCurrencyString,
  classHelper,
} from 'utils/helper';
import AttributeDropdown from '../../AttributeDropdown';
import MinimumConsumption from '../../MinimumConsumption';
import TicketInformationV02 from '../../TicketInformationV02';
import NoShowFee from './NoShowFee';
import TicketInformation from './TicketInformation';
import { CartItem } from 'App/Experiences/types/addOnOrder';
import RegularTableButton from './RegularTableButton';
import { CustomDataValue } from 'types/shared/helper/customElements';
import ExperienceVoucher from './ExperienceVoucher';
import axios from 'axios';
import CommentModal from 'Components/Molecules/CommentModal';
import Warning from '../../Warning';
import useRestaurant from 'CustomHooks/useRestaurant';
import EditableField from './EditableField';

export type ReservationDetailsProp = {
  reservation: Reservation;
  occassions?: { id: string; title: string }[];
  onReservationAction: (
    id: string,
    type: ReservationActionTypes,
    payload?: any,
  ) => void;
  setHasOverflow: (value: { bottom: boolean; top: boolean }) => void;
  handleScrolling?: (
    event: React.UIEvent<HTMLDivElement | HTMLLabelElement | HTMLFormElement>,
  ) => void;
};

function toRelationshipId(guestId: string | undefined, restaurantId: string) {
  if (!guestId) {
    return null;
  } else if (guestId.endsWith(`-${restaurantId}`)) {
    return guestId;
  } else {
    return `${guestId}-${restaurantId}`;
  }
}

export function stringifyCustomDataValue(value: CustomDataValue, key?: string) {
  if (key === 'balance') {
    return toCurrencyString(value as number, '€', false, 1);
  }
  if (typeof value === 'string') {
    if (value.startsWith('http')) {
      return (
        <a href={value} target="_blank" rel="noreferrer">
          Anhang
        </a>
      );
    }
    return value;
  } else if (typeof value === 'number') {
    return value;
  } else if (typeof value === 'boolean') {
    return value ? 'Ja' : 'Nein';
  } else {
    return '';
  }
}

const ReservationDetails = ({
  reservation,
  occassions = [],
  onReservationAction = noop,
  setHasOverflow,
  handleScrolling = noop,
}: ReservationDetailsProp) => {
  const occassionString =
    occassions?.find((o) => o.id === reservation.occassion)?.title ||
    capitalizeWord(reservation.occassion);

  const detailsFields = useMemo(() => {
    return [
      {
        label: 'Status',
        value: capitalizeWord(reservation.currentStatus),
      },
      {
        label: 'Date',
        value: reservation.date ? toEuropeanDate(reservation.date) : '-',
      },
      {
        label: 'Occassion',
        value: occassionString,
        align: 'right',
      },
      {
        label: 'Telephone',
        value: reservation.guest?.phone,
      },
      {
        label: 'Time',
        value: reservation.time,
      },
      {
        label: 'Reservation Length',
        value: blockToTime(reservation.reservationLength || 0),
      },
      {
        label: 'Persons',
        value: reservation.guests,
      },
      {
        label: 'Tables',
        value:
          reservation?.tables && reservation?.tables?.length > 0
            ? reservation?.tables?.join(', ')
            : reservation?.tableStr ?? '',
      },
      {
        label: 'Custom Id',
        value: reservation.customId,
      },
    ];
  }, [reservation]);

  useEffect(() => {
    const container = document.getElementById('details-container');
    if (!!container) {
      const overFlow = container.clientHeight < container.scrollHeight;
      setHasOverflow({ bottom: overFlow, top: false });
    }
  }, [setHasOverflow]);

  const { experimentalFlags, globalFlags, projectId } = useRestaurant();

  const [relationship] = useDocument<RelationshipDocument>(
    'relationships',
    toRelationshipId(
      reservation.guest?.id || reservation.guest?.guestId,
      reservation.restaurant,
    ),
  );

  const guestComment =
    relationship?.data?.guestComment ?? reservation.guest?.guestComment ?? '';

  useEffect(() => {
    if (
      !(reservation.guest?.id || reservation.guest?.guestId) &&
      !reservation.walkIn &&
      !reservation.validTill
    ) {
      axios.post(
        `https://europe-west3-${projectId}.cloudfunctions.net/createGuestWhenMissing`,
        {
          reservationId: reservation.id,
        },
      );
    }
  }, [reservation.guest?.id, projectId]);

  const cancelWarning = async (text: string) => {
    onReservationAction(
      reservation.id,
      ReservationActionTypes.CANCEL_WARNING,
      text,
    );
  };

  return (
    <Box
      className="reservation-side-bar"
      id="details-container"
      style={
        ['failed', 'done'].includes(reservation?.currentStatus || '')
          ? { maxHeight: 'calc(100% - 50px)' }
          : { maxHeight: 'calc(100% - 150px)' }
      }
      onScroll={(e) => handleScrolling(e)}
    >
      <Warning id={reservation.id} />
      {/* Grace-period indicator: ticket reservation is held but Mollie
          payment hasn't come in yet. Table is blocked, but staff should
          know the guest may still abandon the payment. */}
      {!!reservation.ticketId &&
        !!(reservation as any)?.validTill &&
        (reservation as any).validTill > Date.now() &&
        reservation.status === 'success' &&
        (reservation as any)?.cancelationType !== 'payment_abandoned' && (
          <Box
            className="reservation-field"
            style={{
              padding: '12px 14px',
              margin: '8px 0',
              borderRadius: 8,
              border: '1px dashed #f4b02a',
              backgroundColor: 'rgba(244, 176, 42, 0.08)',
            }}
          >
            <Typography variant="text-2" color="textPrimary" block>
              Zahlung ausstehend – Tisch ist vorgemerkt
            </Typography>
            <Typography variant="text-3" color="subdued" block>
              Der Gast hat das Ticket noch nicht bezahlt. Der Tisch ist bis zum
              Ablauf der Bezahlfrist reserviert.
            </Typography>
          </Box>
        )}
      {/* Abgebrochen vs Storniert: payment-abandoned reservations are those
          where the guest didn't finalize the Mollie payment in time. They
          aren't real cancellations — no email was sent to the guest and the
          typical "Storniert" label would be misleading. Show a distinct
          notice to the restaurateur. */}
      {(reservation as any)?.cancelationType === 'payment_abandoned' && (
        <Box
          className="reservation-field"
          style={{
            padding: '12px 14px',
            margin: '8px 0',
            borderRadius: 8,
            border: '1px solid #f4b02a',
            backgroundColor: 'rgba(244, 176, 42, 0.12)',
          }}
        >
          <Typography variant="text-2" color="textPrimary" block>
            Abgebrochen — Zahlung nicht abgeschlossen
          </Typography>
          <Typography variant="text-3" color="subdued" block>
            Der Gast hat die Ticketzahlung nicht rechtzeitig abgeschlossen. Es
            wurde keine Stornierungsmail versendet.
          </Typography>
        </Box>
      )}
      {/* Seat-conflict warning: a late payment arrived after the reservation
          was marked FAILED, and the original table is now held by another
          reservation. The reservation was resurrected without a table and
          needs manual placement. */}
      {!!(reservation as any)?.paymentArrivedButNoSeat && (
        <Box
          className="reservation-field"
          style={{
            padding: '12px 14px',
            margin: '8px 0',
            borderRadius: 8,
            border: '1px solid #b10b45',
            backgroundColor: 'rgba(177, 11, 69, 0.08)',
          }}
        >
          <Typography variant="text-2" color="textPrimary" block>
            Zahlung eingegangen — bitte manuell platzieren
          </Typography>
          <Typography variant="text-3" color="subdued" block>
            Die Zahlung ist eingegangen, aber der ursprüngliche Tisch ist
            inzwischen vergeben. Bitte einen freien Tisch zuweisen.
          </Typography>
        </Box>
      )}
      {!!reservation.ticketId && (
        <TicketInformationV02 reservation={reservation} />
      )}
      {!!reservation.minimumConsumption && (
        <MinimumConsumption reservation={reservation} />
      )}

      <AddOnInformations orders={reservation.orders ?? []} />

      {reservation.customData && (
        <>
          {Object.entries(reservation.customData).map(([key, value]) => (
            <Box className="space-between reservation-field" key={key}>
              <Typography variant="text-3" block style={{ marginBottom: 8 }}>
                {key === 'balance'
                  ? 'Bisher bestellt'
                  : key.replace('_internal', '')}
              </Typography>
              <Typography variant="text-2" color="subdued" textAlign="right">
                {stringifyCustomDataValue(value, key)}
              </Typography>
            </Box>
          ))}
        </>
      )}

      {experimentalFlags.data?.editGuestComment &&
      !globalFlags?.data?.deactivateGuestComment ? (
        <CommentModal
          label="Guest Comment"
          value={reservation.guest?.comment || ''}
          onChange={(value) =>
            onReservationAction(
              reservation.id,
              ReservationActionTypes.COMMENT,
              value,
            )
          }
          id={reservation.id}
          action="comment"
          editableField
          labelTranslation="reservations"
        />
      ) : (
        <Box className="reservation-field">
          <Typography
            variant="text-3"
            translation="reservations"
            block
            style={{ marginBottom: 8 }}
          >
            Guest Comment
          </Typography>
          <Typography
            variant="text-2"
            color="subdued"
            translation="reservations"
          >
            {reservation.guest?.comment || '-'}
          </Typography>
        </Box>
      )}
      <CommentModal
        label="Waiter Comment"
        value={reservation.guest?.hostComment || ''}
        onChange={(value) =>
          onReservationAction(
            reservation.id,
            ReservationActionTypes.HOST_COMMENT,
            value,
          )
        }
        id={reservation.id}
        action="hostComment"
        editableField
        labelTranslation="reservations"
      />
      {!!(reservation.guest?.id || reservation.guest?.guestId) && (
        <CommentModal
          label="Langzeit Kommentar"
          value={guestComment}
          onChange={(value) =>
            onReservationAction(
              reservation.id,
              ReservationActionTypes.GUEST_COMMENT,
              {
                guestComment: value,
                relationshipId:
                  reservation.guest?.id || reservation.guest?.guestId,
              },
            )
          }
          id={reservation.id}
          action="guestComment"
          editableField
          labelTranslation="reservations"
        />
      )}
      {reservation.additional && (
        <TicketInformation {...reservation.additional} />
      )}
      {reservation.experienceVoucher && (
        <ExperienceVoucher {...reservation.experienceVoucher} />
      )}

      {detailsFields.map((f, i) => (
        <Box
          key={i}
          className={`space-between reservation-field ${
            String(f?.value ?? '').length > 30 ? 'flex-start' : ''
          }`}
        >
          <Typography variant="text-3" translation="reservations">
            {f.label}
          </Typography>
          <Typography
            variant={String(f?.value ?? '').length > 20 ? 'text-3' : 'text-2'}
            color="subdued"
            translation={f.label === 'Status' ? 'reservations' : null}
            textAlign={f.align as any}
          >
            {f.value}
          </Typography>
        </Box>
      ))}

      {!!reservation.noShowFee && <NoShowFee {...reservation.noShowFee} />}
      <Box className="reservation-field">
        <Typography
          variant="text-3"
          translation="reservations"
          block
          style={{ marginBottom: 8 }}
        >
          Attributes
        </Typography>
        <AttributeDropdown
          value={reservation.guest?.attr || []}
          onChange={(newValue) =>
            onReservationAction(
              reservation.id,
              ReservationActionTypes.ATTR,
              newValue,
            )
          }
        />
      </Box>

      <Box className="reservation-field" flex>
        <Checkbox
          label=""
          checked={reservation.excludeFromSlots}
          onChange={(e) =>
            onReservationAction(
              reservation.id,
              ReservationActionTypes.EXCLUDE_FROM_SLOTS,
              e.target.checked,
            )
          }
          color="primary"
          className="reservation-checkbox"
        />
        <Typography variant="text-3" translation="reservations">
          Exclude from reservation slots
        </Typography>
      </Box>
      <RegularTableButton reservation={reservation} />
    </Box>
  );
};

export default ReservationDetails;

// Orders come from the day-scoped addOnOrders listener behind the reservation
// list. A per-reservation query here was denied for non-admins (the read rule
// needs restaurantId in the query) and would cost a second read per open.
const AddOnInformations: React.FC<{
  orders: NonNullable<Reservation['orders']>;
}> = ({ orders }) => {
  const filteredOrders = orders.filter(
    (x) => x.status === 'succedded' || x.status === 'processing',
  );

  const total = filteredOrders.reduce(
    (acc, cur) => acc + cur.total - (cur.refundedAmount ?? 0),
    0,
  );

  // A fully refunded order stays listed, struck through, without label or fee.
  const openOrders = filteredOrders.filter((x) =>
    x.cart.some((item) => !item.refunded),
  );

  const serviceFee = openOrders.reduce(
    (acc, cur) => acc + (cur.serviceFee ?? 0),
    0,
  );

  const pending = filteredOrders.some((x) => x.status === 'processing');

  const addOns = filteredOrders.reduce((acc, cur) => {
    return [...acc, ...cur.cart.map((x) => ({ ...x, status: cur.status }))];
  }, [] as (CartItem & { status: 'missing_payments' | 'processing' | 'failed' | 'succedded' })[]);

  if (!filteredOrders || filteredOrders.length === 0) return <></>;

  return (
    <div>
      <Box
        className="space-between"
        style={{ padding: '16px 16px 0 16px', gap: 8 }}
      >
        <Typography
          className="colon"
          variant="text-3"
          translation="reservations"
        >
          Add-Ons
        </Typography>
        {openOrders.length > 0 && (
          <Box
            inlineFlex
            className={classHelper([
              'add-on-status',
              pending ? 'add-on-status--processing' : 'add-on-status--paid',
            ])}
          >
            <Typography variant="text-4" translation="reservations">
              {pending ? 'Processing short' : 'Paid short'}
            </Typography>
          </Box>
        )}
      </Box>
      <Box className="space-between reservation-field no-border">
        <Typography variant="text-3" translation="reservations">
          Price paid
        </Typography>
        <Typography variant="text-2" color="subdued" translation={null}>
          {toCurrencyString(total * 100)}
        </Typography>
      </Box>
      {serviceFee > 0 && (
        <Box className="space-between reservation-field no-border">
          <Typography variant="text-3" translation="reservations">
            Service fee
          </Typography>
          <Typography variant="text-2" color="subdued" translation={null}>
            {toCurrencyString(serviceFee * 100)}
          </Typography>
        </Box>
      )}
      {addOns.map((o) => (
        <Box
          key={o.id}
          className={classHelper([
            'reservation-field',
            (o.type === 'simple' || o.type === 'table_select') &&
              'space-between',
          ])}
        >
          <Typography
            variant="text-3"
            translation={null}
            className={o.refunded ? 'line-through' : ''}
            color={o.status === 'failed' ? 'error' : 'textPrimary'}
          >
            {o.title}
          </Typography>
          {o.type === 'simple' && (
            <Typography
              variant="text-2"
              color="subdued"
              className={o.refunded ? 'line-through' : ''}
              translation={null}
            >
              {toCurrencyString(o.total * 100)}
            </Typography>
          )}
          {o.type === 'select' && (
            <Box
              className={
                o.refunded ? 'space-between line-through' : 'space-between'
              }
            >
              <Typography variant="text-2" color="subdued" translation={null}>
                {o.choice.title}
              </Typography>
              <Typography variant="text-2" color="subdued" translation={null}>
                {toCurrencyString(o.total * 100)}
              </Typography>
            </Box>
          )}
          {o.type === 'multiple_select' && (
            <Box className={o.refunded ? 'line-through' : ''}>
              {o.choices.map((c) =>
                c.count ? (
                  <Box key={c.id} className="space-between">
                    <Typography
                      variant="text-2"
                      color="subdued"
                      translation={null}
                    >
                      {c.count}x {c.title}
                    </Typography>
                    <Typography
                      variant="text-2"
                      color="subdued"
                      translation={null}
                    >
                      {toCurrencyString(c.price.amount * c.count * 100)}
                    </Typography>
                  </Box>
                ) : (
                  <React.Fragment key={c.id}></React.Fragment>
                ),
              )}
            </Box>
          )}
          {!!o.internalNote && (
            <Typography
              variant="text-4"
              translation={null}
              className={o.refunded ? 'line-through' : ''}
              color={o.status === 'failed' ? 'error' : 'subdued'}
            >
              {o.internalNote}
            </Typography>
          )}
        </Box>
      ))}
    </div>
  );
};
